"use client";

import { useAuth } from "@/components/AuthContext";
import TranscriptionView from "@/components/TranscriptionView";
import VoiceAgent from "@/components/VoiceAgent";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import { TrackedRoomAudioRenderer } from "@/components/livekit/TrackedRoomAudioRenderer";
import languages from "@/data/languages.json";
import useLiveKitAgent from "@/hooks/useLiveKitAgent";
import { RoomContext } from "@livekit/components-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown, GraduationCap, Mic, Search, StopCircle } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { DemoVideo } from "./DemoVideo";

// Convert languages.json to array format
const allLanguages = Object.entries(languages as Record<string, string>).map(([name, code]) => ({
  name,
  code,
}));

// Popular languages for quick selection
const popularLanguageCodes = [
  "es-ES",
  "fr-FR",
  "de-DE",
  "it-IT",
  "pt-BR",
  "ja-JP",
  "ko-KR",
  "cmn-CN",
];

const MAX_DEMO_SECONDS = 300; // 5 minutes

export function LandingHero() {
  const [selectedLanguage, setSelectedLanguage] = useState<{
    name: string;
    code: string;
  } | null>(null);
  const [showDemo, setShowDemo] = useState(false);
  const [interactionMode, setInteractionMode] = useState<"passive" | "text" | "voice">("passive");
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLanguageLevel, setSelectedLanguageLevel] = useState<
    "absolute_beginner" | "beginner" | "intermediate" | "advanced"
  >("beginner");
  const heroRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const { user } = useAuth();

  const {
    room,
    isConnected,
    isRecording,
    recordingTime,
    toggleRecording,
    disconnectFromLiveKit,
    connectionState,
    connectionError,
    retryConnection,
    audioTrackError,
    audioRetryCount,
    retryAudioTrack,
    clearAudioErrors,
  } = useLiveKitAgent({
    selectedLanguageCode: selectedLanguage?.code || "",
    selectedLanguageLevel,
    selectedVoice: "Aoede",
    selectedTeachingStyle: "friendly",
    allowUnauthenticatedSessions: true,
    guestSessionLimitSeconds: MAX_DEMO_SECONDS,
    sessionMode: "conversation",
    setInteractionMode,
    subscribed: false,
  });

  // Popular languages for quick buttons
  const popularLanguages = useMemo(() => {
    return allLanguages.filter((lang) => popularLanguageCodes.includes(lang.code));
  }, []);

  // Select language (doesn't start demo)
  const selectLanguage = (lang: { name: string; code: string }) => {
    setSelectedLanguage(lang);
    setShowDropdown(false);
    setSearchQuery("");
  };

  // Start demo session (user clicks button)
  const startDemo = () => {
    if (selectedLanguage) {
      setShowDemo(true);
      toggleRecording("conversation");
    }
  };

  // Auto-end after 5 minutes
  useEffect(() => {
    if (isRecording && recordingTime >= MAX_DEMO_SECONDS) {
      endDemo();
    }
  }, [isRecording, recordingTime]);

  const endDemo = () => {
    disconnectFromLiveKit(false, false, "landing_demo_end");
    setShowDemo(false);
    setSelectedLanguage(null);
  };

  const remaining = Math.max(0, MAX_DEMO_SECONDS - (recordingTime || 0));
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  // Handle URL query params for auto-selection
  const searchParams = useSearchParams();
  useEffect(() => {
    const languageParam = searchParams.get("language");
    if (languageParam) {
      const foundLang = allLanguages.find(
        (l) => l.name.toLowerCase() === languageParam.toLowerCase()
      );
      if (foundLang) {
        setSelectedLanguage(foundLang);
        // Optional: wait a tick then start demo if desired, but user request just said "select"
        // To auto-start:
        // setShowDemo(true);
        // setIsMuted(true);
        // toggleRecording("conversation");

        // Scroll hero into view so they see it's selected
        heroRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    // Only run on mount (searchParams intentionally excluded)
  }, []);

  return (
    <section id="demo" className="relative overflow-x-clip bg-[#F5F5F0]">
      <div
        ref={heroRef}
        className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-16 lg:px-8 lg:py-20"
      >
        <div
          className={`grid min-w-0 items-center gap-10 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:gap-14 ${showDemo ? "hidden" : ""}`}
        >
          {/* Left Column: Text & Interaction */}
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="min-w-0 text-left"
          >
            <div className="mb-7">
              <p className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-[#A8431F]">
                <Mic className="h-4 w-4 text-orange-500" />
                Free 5-minute voice demo
              </p>
              <h1 className="max-w-[16ch] text-4xl font-bold leading-[1.04] tracking-[-0.045em] text-[#1C1C1E] sm:text-5xl lg:text-6xl">
                Speak with more confidence.
              </h1>

              {/* Subheadline */}
              <motion.p
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mt-5 max-w-[52ch] text-lg leading-7 text-gray-600 sm:text-xl"
              >
                Have voice conversations with an AI tutor in 70+ languages, with feedback that helps
                you keep the conversation moving.
              </motion.p>
            </div>

            {/* Language Selection & Demo Start */}
            <div className="w-full max-w-xl">
              {/* Search Dropdown */}
              <div className="mb-6 text-left">
                <ResponsiveLanguagePicker
                  open={showDropdown}
                  onOpenChange={setShowDropdown}
                  trigger={
                    <button
                      type="button"
                      className="flex w-full min-w-0 items-center gap-3 rounded-xl border-2 border-gray-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-orange-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] focus-visible:ring-offset-2"
                    >
                      <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      <span
                        className={`min-w-0 flex-1 truncate ${selectedLanguage ? "text-gray-900" : "text-gray-400"}`}
                      >
                        {selectedLanguage?.name ?? "Choose a language for your free demo"}
                      </span>
                      <ChevronDown
                        className={`h-5 w-5 text-gray-400 transition-transform ${showDropdown ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                  }
                  languages={languages as Record<string, string>}
                  selectedLanguageCode={selectedLanguage?.code ?? ""}
                  onSelectLanguage={(code) => {
                    const language = allLanguages.find((entry) => entry.code === code);
                    if (language) selectLanguage(language);
                  }}
                  searchTerm={searchQuery}
                  onSearchTermChange={setSearchQuery}
                  popularValues={popularLanguageCodes}
                  title="Choose a demo language"
                />
              </div>

              {/* Controls Container */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                {/* Level Selector */}
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button className="flex items-center justify-center gap-2 px-5 py-3 bg-white hover:bg-gray-50 rounded-xl text-sm font-semibold text-gray-700 hover:text-gray-900 transition-all duration-300 border-2 border-gray-200 hover:border-orange-400 shadow-sm min-w-[140px]">
                      <GraduationCap size={18} className="text-orange-500" />
                      <span className="font-bold text-orange-600">
                        {selectedLanguageLevel
                          .split("_")
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(" ")}
                      </span>
                      <ChevronDown size={16} className="text-gray-400" />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content className="z-[10000] bg-white border-2 border-orange-200 rounded-xl shadow-xl py-2 mt-2 min-w-[180px]">
                    {(["absolute_beginner", "beginner", "intermediate", "advanced"] as const).map(
                      (level) => (
                        <DropdownMenu.Item
                          key={level}
                          onSelect={() => setSelectedLanguageLevel(level)}
                          className={`px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-orange-50 hover:text-orange-600 cursor-pointer transition-all duration-200 mx-1 ${
                            selectedLanguageLevel === level
                              ? "bg-orange-50 text-orange-600"
                              : "text-gray-700"
                          }`}
                        >
                          {level
                            .split("_")
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(" ")}
                        </DropdownMenu.Item>
                      )
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>

                {/* Start Button */}
                <motion.button
                  whileTap={selectedLanguage && !shouldReduceMotion ? { scale: 0.98 } : undefined}
                  onClick={startDemo}
                  disabled={!selectedLanguage}
                  className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#FD954F] px-8 py-3 text-lg font-bold text-[#1C1C1E] shadow-lg shadow-orange-500/20 transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none sm:flex-none"
                >
                  <Mic className="h-5 w-5" />
                  Start free demo
                </motion.button>
              </div>
              {/* Quick Select Text */}
              {!selectedLanguage && (
                <p className="text-sm text-gray-500 mt-4 text-center lg:text-left">
                  Or select a popular language:
                  <span className="flex flex-wrap justify-center lg:justify-start gap-2 mt-2">
                    {popularLanguages.slice(0, 4).map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => selectLanguage(lang)}
                        className="text-gray-600 hover:text-orange-600 underline decoration-gray-300 hover:decoration-orange-300 underline-offset-2 transition-all"
                      >
                        {lang.name}
                      </button>
                    ))}
                  </span>
                </p>
              )}
            </div>
          </motion.div>

          {/* Right Column: approved product walkthrough */}
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="min-w-0 w-full rounded-2xl border border-gray-200 bg-white p-2 shadow-[0_24px_70px_rgba(92,53,27,0.12)] sm:p-3"
          >
            <DemoVideo
              src="/ui/chickytutor-how-it-works4.mp4"
              poster="/ui/chickytutor-how-it-works4-thumbnail.jpg"
              appearance="brand"
            />
          </motion.div>
        </div>

        {/* Stats Section */}
        <div
          className={`mt-12 grid grid-cols-3 gap-3 border-t border-gray-300 pt-6 md:mt-16 md:gap-8 ${showDemo ? "hidden" : ""}`}
        >
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 md:text-4xl">70+</div>
            <p className="text-sm md:text-base text-gray-500 mt-1">Languages</p>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">5 min</div>
            <p className="text-sm md:text-base text-gray-500 mt-1">Free Demo</p>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">24/7</div>
            <p className="text-sm md:text-base text-gray-500 mt-1">Available</p>
          </div>
        </div>

        {/* Full Screen Demo Interface (Overlays when active) */}
        <AnimatePresence>
          {showDemo && (
            <motion.div
              initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="max-w-4xl mx-auto"
            >
              {/* Demo Container */}
              <div className="overflow-hidden rounded-2xl border border-white/15 bg-[#0F0F14]">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-white/10 bg-[#121318] px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#FD954F] p-0.5">
                      <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center">
                        <Image src="/ui/Chickylogovector.gif" alt="Chicky" width={24} height={24} />
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-white flex items-center gap-2">
                        {selectedLanguage?.name} Practice
                      </div>
                      <div className="text-gray-400 text-sm">5-minute demo session</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Level Dropdown */}
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button className="flex items-center gap-2 rounded-lg border border-white/15 bg-[#15161B] px-3 py-2 text-sm font-semibold text-gray-200 transition-colors hover:border-[#FD954F]/50 hover:bg-[#1B1C22] hover:text-white">
                          <GraduationCap size={16} className="text-[#FD954F]" />
                          <span className="hidden sm:inline">Level:</span>
                          <span className="font-bold text-[#FD954F]">
                            {selectedLanguageLevel
                              .split("_")
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(" ")}
                          </span>
                          <ChevronDown size={14} className="text-gray-400" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content className="z-[10000] mt-2 min-w-[160px] rounded-xl border border-white/15 bg-[#15161B] py-2 shadow-xl shadow-black/30">
                        {(
                          ["absolute_beginner", "beginner", "intermediate", "advanced"] as const
                        ).map((level) => (
                          <DropdownMenu.Item
                            key={level}
                            onSelect={() => setSelectedLanguageLevel(level)}
                            className={`mx-1 cursor-pointer rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-[#FD954F]/10 hover:text-[#FD954F] ${
                              selectedLanguageLevel === level
                                ? "bg-[#FD954F]/10 text-[#FD954F]"
                                : "text-white"
                            }`}
                          >
                            {level
                              .split("_")
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(" ")}
                          </DropdownMenu.Item>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>

                    {/* Timer */}
                    <div className="rounded-full border border-white/15 bg-[#15161B] px-4 py-2 font-mono text-sm text-[#FD954F]">
                      {mm}:{ss}
                    </div>
                    {/* End Button */}
                    <button
                      onClick={endDemo}
                      className="flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-gray-300 transition-colors hover:bg-[#1B1C22] hover:text-white"
                    >
                      <StopCircle className="w-4 h-4" />
                      End
                    </button>
                  </div>
                </div>

                {/* Transcription Area */}
                <div className="h-[400px] md:h-[500px] relative text-left">
                  <RoomContext.Provider value={room}>
                    {isRecording && isConnected && (
                      <TrackedRoomAudioRenderer
                        activeSession={isRecording && isConnected}
                        roomName={room.name || null}
                        surface="landing-hero"
                      />
                    )}
                    {/* Voice Agent Visualizer */}
                    <div className="flex items-center justify-center border-b border-white/10 bg-[#121318] py-4">
                      <VoiceAgent
                        room={room}
                        isRecording={Boolean(isRecording)}
                        renderAudioRenderer={false}
                      />
                    </div>

                    {/* Transcription View */}
                    <div className="h-[calc(100%-80px)] overflow-y-auto">
                      <TranscriptionView
                        onClose={endDemo}
                        connectionState={connectionState}
                        connectionError={connectionError}
                        retryConnection={retryConnection}
                        audioTrackError={audioTrackError}
                        audioRetryCount={audioRetryCount}
                        retryAudioTrack={retryAudioTrack}
                        clearAudioErrors={clearAudioErrors}
                        sessionDuration={recordingTime}
                        selectedLanguageCode={selectedLanguage?.code || ""}
                        selectedLanguageLevel={selectedLanguageLevel}
                        isActiveSession={Boolean(isRecording && isConnected)}
                        showInactiveSessionRecovery
                        recoverySurface="landing_hero"
                        renderRoomAudio={false}
                      />
                    </div>
                  </RoomContext.Provider>
                </div>
              </div>

              {/* CTA after demo */}
              <motion.div
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 text-center"
              >
                <p className="text-gray-600 mb-4">
                  {user
                    ? "Continue practicing in the app"
                    : "Want unlimited practice time and all features?"}
                </p>
                <a
                  href={user ? "/" : "/auth"}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {user ? "Go to App" : "Sign up for free"}
                </a>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
